// --- React & Redux
import React from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
// --- Actions & Hooks
import {
  useTPRExceptionsData,
  beginTprExceptionsFetch,
  isLatestTprExceptionsFetch,
} from "./hooks/useTPRExceptionsData";
import { useLoading } from "../../utils/hooks/useLoading";
import { useToasts } from "../../utils/hooks/useToasts";
import { useStoredItemsPerPageSync } from "tap/utils/hooks/useStoredItemsPerPageSync.js";
import { PAGINATION_STORAGE_KEYS } from "tap/utils/paginationPreferences.js";
// --- External UI Libraries
import Button from "@dtci/spellbook/Button";
import Typography from "@dtci/spellbook/Typography";
import { Col, Row } from "antd";
// --- Components
import { PaginationBar } from "../../components/PaginationBar";
import ToastNotificationProvider from "../../components/ToastNotifications";
import { TPRExceptionsGrid } from "./components/grid";
import { TPRExceptionsSearchForm } from "./components/search-form";
import TwoRowsLayout from "./two-row";
// --- Utilities
import { exportToCsvWithHeaders } from "../../utils/exportToCsv";
import { getAllItemsRange, getItemRange } from "../../utils/pagination";
import { parseSearchParams, updateSearchParams } from "../../utils/urlParams";
import { tapApi } from "../../api";
import { isDismissApiError } from "./utils/userOptionsUtils";
import { buildTprExceptionsPageUpdate } from "./utils/paginationUtils";
// --- Config
import { headers, renamedHeaders } from "./config/csvHeaders";
// --- Styles
import "./styles.scss";

export const TPRExceptionsPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { setLoadingTrue, setLoadingFalse } = useLoading();
  const { showErrorToast } = useToasts();
  const {
    fetchTPRExceptions,
    appendSessionUserToDismissedByFilter,
    updateTPRExceptions,
    itemsPerPage,
    tprExceptionsRange,
  } = useTPRExceptionsData();

  const {
    totalPages,
    currentPage,
    totalItems,
    pageSizeOptions,
    tprExceptions,
  } = useSelector((s) => s.tprExceptions);

  useStoredItemsPerPageSync({
    storageKey: PAGINATION_STORAGE_KEYS.tprExceptions,
    itemsPerPage,
    rangeKey: "tprExceptionsRange",
    onSync: updateTPRExceptions,
  });

  const handleChange = async (pageNumber) => {
    if (pageNumber === currentPage) return;

    const fetchId = beginTprExceptionsFetch();
    const nextPageRange = getItemRange(pageNumber, itemsPerPage);
    const data = await fetchTPRExceptions(nextPageRange);

    if (!isLatestTprExceptionsFetch(fetchId)) return;

    if (data) {
      updateTPRExceptions(
        buildTprExceptionsPageUpdate(data, {
          itemsPerPage,
          currentPage: pageNumber,
          range: nextPageRange,
          previousTotalItems: totalItems,
          preserveTotal: true,
        }),
      );
    }
  };

  const handlePageSizeChange = (newSize) => {
    const currentParams = parseSearchParams(location.search);
    updateSearchParams(location, navigate, {
      ...currentParams,
      pageSize: newSize,
    });
  };

  const handleExport = async () => {
    const data = await fetchTPRExceptions(getAllItemsRange(totalItems));

    if (!data) return;

    const filtered = (data?.content ?? []).map((item) =>
      headers.reduce((obj, key) => {
        obj[key] = item[key];
        return obj;
      }, {}),
    );

    exportToCsvWithHeaders(filtered, headers, renamedHeaders, "tprExceptions");
  };

  const handleDismiss = async (rowData) => {
    if (rowData?.exceptionStatus?.toUpperCase() === "RESOLVED") {
      return;
    }
    try {
      setLoadingTrue();
      const isDismissing = rowData?.dismissed !== true;
      const response = await tapApi.dismissTprException(
        rowData.id,
        isDismissing,
      );

      if (isDismissApiError(response)) {
        showErrorToast(
          response?.message ||
            response?.error?.message ||
            "Failed to dismiss TPR Exception.",
        );
        return;
      }

      if (isDismissing) {
        appendSessionUserToDismissedByFilter();
      }

      updateTPRExceptions({ refreshTPRExceptions: Date.now() });
    } catch (error) {
      showErrorToast(error?.message || "Failed to dismiss TPR Exception.");
      console.error("Error dismiss TPR Exception:", error);
    } finally {
      setLoadingFalse();
    }
  };

  return (
    <>
      <Row className="tpr-exceptions-page-header">
        <Col>
          <Typography variant="T50" className="tpr-exceptions-page-title">
            TPR Exceptions
          </Typography>
        </Col>
        <Col className="tpr-exceptions-page-actions">
          <Button
            iconType="fa-solid fa-arrow-down-to-line"
            labelText="Export"
            styleType="link"
            onClick={handleExport}
            disabled={totalItems === 0}
          >
            Export
          </Button>
          <Button
            iconType="fa-solid fa-rotate"
            labelText="Refresh"
            styleType="link"
            onClick={() =>
              updateTPRExceptions({ refreshTPRExceptions: Date.now() })
            }
          >
            Refresh
          </Button>
        </Col>
      </Row>
      <TwoRowsLayout>
        <main data-position="top">
          <TPRExceptionsSearchForm />
        </main>
        <main data-position="bottom">
          <TPRExceptionsGrid onDismiss={handleDismiss} />
        </main>
      </TwoRowsLayout>
      <PaginationBar
        totalPages={totalPages || 0}
        currentPage={currentPage}
        totalItems={totalItems}
        itemsPerPage={itemsPerPage}
        onPageChange={handleChange}
        onPageSizeChange={handlePageSizeChange}
        pageSizeOptions={pageSizeOptions}
      />
      <ToastNotificationProvider />
    </>
  );
};
